import React, { useState } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Cell,
} from "recharts";
import Icon from "components/AppIcon";

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload?.length) {
    const d = payload?.[0]?.payload;
    return (
      <div
        className="rounded-lg border p-3 text-xs"
        style={{ background: "var(--color-card)", borderColor: "var(--color-border)", boxShadow: "var(--shadow-lg)" }}
      >
        <p className="font-heading font-semibold mb-1" style={{ color: "var(--color-foreground)" }}>{d?.segment}</p>
        <p className="font-caption" style={{ color: "var(--color-text-secondary)" }}>
          Churn Risk: <span className="font-data font-medium" style={{ color: "#DC2626" }}>{d?.risk}%</span>
        </p>
        <p className="font-caption" style={{ color: "var(--color-text-secondary)" }}>
          At-Risk Customers: <span className="font-data font-medium" style={{ color: "var(--color-foreground)" }}>{d?.count}</span>
        </p>
        <p className="font-caption" style={{ color: "var(--color-text-secondary)" }}>
          ARR at Risk: <span className="font-data font-medium" style={{ color: "#D97706" }}>₹{(d?.arr / 100000)?.toFixed(1)}L</span>
        </p>
      </div>
    );
  }
  return null;
};

function getRiskColor(score) {
  if (score >= 80) return "#DC2626";
  if (score >= 70) return "#D97706";
  return "#F59E0B";
}

export default function ChurnPrediction({ churnData: churnDataProp, atRiskCustomers: atRiskCustomersProp }) {
  const [showAll, setShowAll] = useState(false);
  const riskBySegment = (churnDataProp || []).map((item) => ({
    segment: item.segment,
    risk: item.riskPercent,
    count: item.atRiskCount,
    arr: item.arrAtRisk,
  }));
  const riskAccounts = atRiskCustomersProp || [];
  const totalAtRisk = riskBySegment.reduce((acc, item) => acc + (item.count || 0), 0);
  const totalArrAtRisk = riskBySegment.reduce((acc, item) => acc + (item.arr || 0), 0);
  const displayed = showAll ? riskAccounts : riskAccounts?.slice(0, 3);

  return (
    <div
      className="rounded-xl border"
      style={{
        background: "var(--color-card)",
        borderColor: "var(--color-border)",
        boxShadow: "var(--shadow-md)",
        padding: "20px",
      }}
    >
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <h3 className="font-heading font-semibold text-base" style={{ color: "var(--color-foreground)" }}>
            Churn Prediction Analytics
          </h3>
          <p className="font-caption text-xs mt-0.5" style={{ color: "var(--color-text-secondary)" }}>
            AI-powered churn risk by segment &amp; top at-risk accounts
          </p>
        </div>
        <div
          className="flex items-center gap-1.5 px-2.5 py-1 rounded-full"
          style={{ background: "#FEE2E2" }}
        >
          <Icon name="AlertTriangle" size={12} color="#DC2626" />
          <span className="font-caption text-xs font-semibold" style={{ color: "#DC2626" }}>
            {totalAtRisk} at risk · ₹{(totalArrAtRisk / 10000000).toFixed(1)}Cr ARR
          </span>
        </div>
      </div>
      <div className="flex flex-col lg:flex-row gap-5">
        {/* Bar chart */}
        <div className="flex-1 min-w-0 h-48" aria-label="Churn risk by segment bar chart">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={riskBySegment} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis dataKey="segment" tick={{ fontSize: 11, fill: "var(--color-text-secondary)", fontFamily: "var(--font-caption)" }} />
              <YAxis tick={{ fontSize: 11, fill: "var(--color-text-secondary)", fontFamily: "var(--font-caption)" }} unit="%" />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="risk" radius={[4, 4, 0, 0]}>
                {riskBySegment?.map((d, i) => (
                  <Cell key={i} fill={d?.risk >= 30 ? "#DC2626" : d?.risk >= 15 ? "#D97706" : "#F59E0B"} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* At-risk list */}
        <div className="lg:w-64 xl:w-72 flex-shrink-0">
          <p className="font-caption text-xs font-semibold mb-2" style={{ color: "var(--color-text-secondary)" }}>
            Top At-Risk Accounts
          </p>
          <div className="flex flex-col gap-2">
            {displayed?.map((c, i) => (
              <div
                key={i}
                className="flex items-center justify-between p-2.5 rounded-lg"
                style={{ background: "var(--color-surface-2)", border: "1px solid var(--color-border)" }}
              >
                <div className="flex-1 min-w-0">
                  <p className="font-caption text-xs font-semibold truncate" style={{ color: "var(--color-foreground)" }}>
                    {c?.name}
                  </p>
                  <p className="font-caption text-xs" style={{ color: "var(--color-text-secondary)" }}>
                    {c?.segment} · {c?.reason}
                  </p>
                  <p className="font-data text-xs" style={{ color: "var(--color-text-secondary)" }}>
                    {c?.daysInactive}d inactive · ₹{(c?.arr / 100000)?.toFixed(1)}L ARR
                  </p>
                </div>
                <div
                  className="flex-shrink-0 ml-2 flex items-center justify-center rounded-full font-data text-xs font-bold"
                  style={{
                    width: 36,
                    height: 36,
                    background: `${getRiskColor(c?.riskScore)}18`,
                    color: getRiskColor(c?.riskScore),
                    border: `1.5px solid ${getRiskColor(c?.riskScore)}`,
                  }}
                >
                  {c?.riskScore}
                </div>
              </div>
            ))}
          </div>
          {riskAccounts?.length > 3 && (
            <button
              onClick={() => setShowAll(!showAll)}
              className="mt-2 w-full font-caption text-xs font-medium py-1.5 rounded-lg transition-all duration-150 hover:bg-muted"
              style={{ color: "var(--color-primary)", border: "1px solid var(--color-border)" }}
            >
              {showAll ? "Show Less" : `+${riskAccounts?.length - 3} more accounts`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}